/**
 * What one press does, as a pure function: `performAction` turns a row of the
 * table (M3.6a) and a card into the records that follow from it, and nothing
 * happens until someone writes them down. No transport, no storage, no DOM
 * here — the store (`store.ts`) appends and the row opens the link.
 *
 * Two rules the shape enforces:
 *
 * - **Nothing sends.** A deep-link action produces a `link` and no
 *   corrections at all; every other action produces corrections and no link.
 * - **A Probably done? answer is a `HistoryConfirmation`, never a
 *   `ProductAction`** (Spec, *Instrumentation*): the two are different fields
 *   of the outcome and one is always null.
 *
 * Corrections are built with `makeCorrection` from the pipeline (M4.2) and
 * read back by `correctionEffect` — one path, appended, never an edit.
 */
import type {
  Correction,
  HistoryConfirmation,
  Ledger,
  LedgerItem,
  LedgerSectionId,
  Party,
  ProductAction,
} from '@cordelia/core'
import { makeCorrection } from '@cordelia/pipeline'
import type { ActionSpec } from './catalog.js'
import { sectionOf } from './catalog.js'
import { threadLinkFor } from './links.js'

export type ActionRequest = {
  readonly spec: ActionSpec
  readonly item: LedgerItem
  readonly ledger: Ledger
  /** What the prompt collected, resolved against the identity graph already. */
  readonly party?: Party | undefined
  /** ISO time of the press. */
  readonly time: string
}

export type ActionOutcome = {
  readonly action: ActionSpec['id']
  readonly section: LedgerSectionId
  /** The thread to open in a new tab. Null for every action that is not a deep link. */
  readonly link: string | null
  /** Appended to the log in this order. Empty for a deep link and for Correct. */
  readonly corrections: readonly Correction[]
  /** An action on an active item. Null for a Probably done? answer. */
  readonly product_action: ProductAction | null
  /** A Probably done? answer. Null for everything else. */
  readonly confirmation: HistoryConfirmation | null
  /** Correct hands off to the correction sheet (M4.1); nothing is appended here. */
  readonly opens_correction_sheet: boolean
}

/** Whether the counterparty is waiting on a reply, for the recorded action. */
export const replyOwed = (ledger: Ledger, item: LedgerItem): boolean =>
  ledger.reply_owed_signals.some((s) => s.item_id === item.id)

export function performAction({ spec, item, ledger, party, time }: ActionRequest): ActionOutcome {
  const section = spec.section
  if (sectionOf(item.surface) !== section)
    throw new Error(`"${spec.id}" is not an action on ${item.surface}`)

  const affects = [item.id, item.claim_id]
  const corrections: Correction[] = []

  // A Probably done? answer records the classification the person gave, so
  // M4.5 can read it back from the same append-only log.
  if (spec.confirmation !== undefined)
    corrections.push(
      makeCorrection({
        contested_field: 'history',
        corrected_value: spec.confirmation,
        affects,
        reason: 'history_confirmation',
        time,
      }),
    )

  if (spec.asksForParty === true)
    corrections.push(
      makeCorrection({
        contested_field: 'responsible_actor',
        corrected_value: party ?? 'unresolved',
        affects,
        reason: 'delegation',
        time,
      }),
    )

  if (spec.observation !== undefined)
    corrections.push(
      makeCorrection({
        contested_field: 'openness',
        corrected_value: spec.observation,
        affects,
        reason: 'user_observation',
        time,
      }),
    )

  if (spec.suppression !== undefined)
    corrections.push(
      makeCorrection({
        contested_field: 'relevance',
        corrected_value: { reason: spec.suppression },
        affects,
        reason: 'suppression',
        time,
      }),
    )

  const record = { item_id: item.id, action: spec.id, section, time }
  return {
    action: spec.id,
    section,
    link: spec.kind === 'deep_link' ? threadLinkFor(ledger, item) : null,
    corrections,
    product_action:
      spec.confirmation === undefined ? { ...record, reply_owed: replyOwed(ledger, item) } : null,
    confirmation:
      spec.confirmation === undefined
        ? null
        : {
            item_id: item.id,
            classification: spec.confirmation,
            time,
            prior_openness_state: item.openness_state,
          },
    opens_correction_sheet: spec.opensCorrectionSheet === true,
  }
}

/**
 * The two undos an append-only log can have (docs/section-actions.md, "No
 * delete exists in the model"). Neither is a verb of the table, so neither
 * records a `ProductAction`: they take back a press rather than making one.
 *
 * `unsuppress` names the suppression it lifts; `reopen` is a later
 * observation that the item is still open, which is what the table's "Still
 * open" appends.
 */
export function undoSuppression(input: {
  readonly item: LedgerItem
  readonly suppression_id: string
  readonly time: string
}): Correction {
  return makeCorrection({
    contested_field: 'relevance',
    corrected_value: input.suppression_id,
    affects: [input.item.id, input.item.claim_id],
    reason: 'unsuppress',
    time: input.time,
  })
}

export function reopen(input: { readonly item: LedgerItem; readonly time: string }): Correction {
  return makeCorrection({
    contested_field: 'openness',
    corrected_value: 'open',
    affects: [input.item.id, input.item.claim_id],
    reason: 'user_observation',
    time: input.time,
  })
}

/**
 * The party a delegation names, resolved against the identity graph by
 * address or display name. Anything the graph does not know stays
 * `unresolved`: the log carries a party, never typed text (Constitution,
 * *Parties and agency* — a party is never free text).
 */
export function resolveParty(text: string, ledger: Ledger): Party {
  const needle = text.trim().toLowerCase()
  if (needle.length === 0) return 'unresolved'
  for (const person of ledger.identity.persons)
    if (
      person.addresses.some((a) => a.toLowerCase() === needle) ||
      person.display_names.some((n) => n.toLowerCase() === needle)
    )
      return person.id
  for (const org of ledger.identity.organizations)
    if (org.name?.toLowerCase() === needle || org.domains.some((d) => d.toLowerCase() === needle))
      return org.id
  return 'unresolved'
}
